import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdMysticalCircuitPanelComponent } from './hd-mystical-circuit-panel.component';
import {
  HD_MYSTICAL_ICON,
  HD_MYSTICAL_PANEL_KEY,
  HdMysticalPanelData,
  hdIsMysticalCircuitGate,
} from './hd-mystical.metadata';

/**
 * Opent het mystiek-paneel (het mystieke circuit met zijn acht poorten).
 *
 * Volgt hetzelfde patroon als `HdMemoryPanelService`: de dock-/dialoogkeuze
 * komt uit de globale `gateInfoOpenBehavior`-setting die de chart-pagina's live
 * doorgeven via {@link setOpenBehavior}. Leestekst, dus altijd de
 * `'full'`-dockzone. Eén paneel; opnieuw openen voor een andere poort
 * hergebruikt het paneel en scrollt via `focusGate` naar de juiste sectie.
 */
@Injectable({ providedIn: 'root' })
export class HdMysticalPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  /** Opent het paneel bovenaan (zonder focus-poort), bv. vanaf de circuits-tab. */
  openForCircuit(origin?: { x: number; y: number }): void {
    this.open(null, origin);
  }

  /** Opent het paneel gescrolld naar de sectie van deze poort. Geen mystieke poort: no-op. */
  openForGate(gate: number, origin?: { x: number; y: number }): void {
    if (!hdIsMysticalCircuitGate(gate)) {
      return;
    }
    this.open(gate, origin);
  }

  private open(focusGate: number | null, origin?: { x: number; y: number }): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    const data: HdMysticalPanelData = { focusGate };

    const ref = this.floatingPanels.openOrFocus<HdMysticalCircuitPanelComponent, HdMysticalPanelData>({
      key: HD_MYSTICAL_PANEL_KEY,
      component: HdMysticalCircuitPanelComponent,
      data,
      dockTab: { label: this.dockTabLabel(), icon: HD_MYSTICAL_ICON },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
    // Ook een al open paneel de nieuwe focus-poort aanreiken (verse referentie
    // zodat de auto-scroll opnieuw wapent, ook bij dezelfde poort).
    ref.replaceData({ ...data });
  }

  /** Kort dock-label ("Mystiek") uit de mystical-scope, met fallback. */
  private dockTabLabel(): string {
    const lang = this.contentLang.effectiveLang();
    const label =
      this.gateInfo.cheatSheetTextIn('mystical', 'dockLabel', lang) ||
      this.gateInfo.cheatSheetTextIn('mystical', 'dockLabel', 'nl');
    return label || 'Mystiek';
  }
}
